import { Stack, Typography, Grid } from "@mui/material";
import React, { useCallback } from "react";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../Components/Misc/ListPageHeader";
import SquareCard from "../../../Components/Cards/SquareCard";
import { useMultipleSelection } from "../../../Utils/Hooks/useMultipleSelection";
import { ModalContext } from "../../../Components/Modals";
import { useState } from "react";
import { assetService, orderService, userService, workflowService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import ModalLoginException, {
  ModalLoginExceptionProps,
} from "../../../Components/Modals/ModalLoginException/modal-login-exception";
import moment from "moment";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";

function ReportingDataExplorer() {
  const { t } = useTranslation();
  const sessions = [
    { id: 1, title: "Users", label: "List of all platform users including permissions" },
    { id: 2, title: "Assets", label: "Created assets in selected range" },
    { id: 3, title: "Orders", label: "Order items run in selected range" },
    { id: 4, title: "Ingests", label: "Ingest workflows run in selected range" },
  ];
  const successMessage = t("alert:success.dataComputingInBackground");

  const { selected, select, isSelected, unselectAll } = useMultipleSelection<number>();
  const [dateIn, setDateIn] = useState<Date | undefined>();
  const [dateOut, setDateOut] = useState<Date | undefined>();

  const selectOnClick = React.useMemo(() => selected.length > 0, [selected]);

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const enqueueCustomSnackbar = useCustomSnackbar();

  const handleSubmit = useCallback(
    (id: number) => {
      if (!dateIn || !dateOut || dateIn > dateOut) {
        if (!openModal) return;

        openModal<ModalLoginExceptionProps>(ModalLoginException, {
          title: t("alert:warning.oopsMessage"),
          message: t("modal:message.selectCorrectStartAndEndDates"),
          advice: "",
        });

        return;
      }

      let _in = moment(dateIn.toString()).format("YYYY-MM-DD");
      let _out = moment(dateOut.toString()).format("YYYY-MM-DD");

      switch (id) {
        case 1:
          userService.DownloadUsersAsCsv().then((response) => {
            enqueueCustomSnackbar(successMessage, "success");
            unselectAll();
          });
          break;
        case 2:
          assetService.DownloadCreatedObjectsByDateRangeAsCSV(_in, _out).then((response) => {
            enqueueCustomSnackbar(successMessage, "success");
            unselectAll();
          });
          break;
        case 3:
          orderService.DownloadOrdersItemsListAsCSV(_in, _out).then((response) => {
            enqueueCustomSnackbar(successMessage, "success");
            unselectAll();
          });
          break;
        case 4:
          workflowService.DownloadInstancesByDefinitionFamilyAsCSV("Ingest", _in, _out).then((response) => {
            enqueueCustomSnackbar(successMessage, "success");
            unselectAll();
          });
          break;
      }
    },
    [dateIn, dateOut]
  );

  return (
    <Stack p={2}>
      <ListPageHeader mb={3}>
        <ListPageHeaderLeft title={t("component:listPageHeaderLeft.title.dataEXporter")} />
        <ListPageHeaderRight>
          <Stack direction="row" spacing={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                value={dateIn ?? undefined}
                label={t("common:startDateLowerCase")}
                onChange={(newValue) => {
                  setDateIn(newValue!);
                }}
                format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
                //renderInput={(params) => <TextField sx={{}} {...params} />}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                value={dateOut ?? undefined}
                label={t("common:endDateLowerCase")}
                onChange={(newValue) => {
                  setDateOut(newValue!);
                }}
                format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
                //renderInput={(params) => <TextField sx={{}} {...params} />}
              />
            </LocalizationProvider>
          </Stack>
        </ListPageHeaderRight>
      </ListPageHeader>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="subtitle1" color="text.tier" fontWeight={400}>
          {t("page:body.dataExportInstruction")}
        </Typography>
      </Stack>
      <Grid container spacing={3}>
        {sessions.map((session, index) => (
          <Grid item xs={3} lg={2} key={index}>
            <SquareCard
              id={session.id}
              title={session.title}
              key={index}
              to={`#`}
              subtitle={`${session.label}`}
              onCheckboxClick={(id) => select(id)}
              onEditClick={() => false}
              onDeleteClick={(id) => false}
              onClick={handleSubmit}
              selected={isSelected(session.id)}
              selectOnClick={selectOnClick}
              selectMode={false}
              maskEdit={true}
              maskDelete={true}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

export default ReportingDataExplorer;
